import type { FC } from 'react';
import { Fragment } from 'react';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import type { ComparisonFrontend } from 'src/types/dataline';
import type { DragItem } from 'src/types/workspace';
import DatalineFragment from 'src/components/common/Dataline/components/Definitionline/components/DatalineFragment';
import LogicalOperatorWrapper from 'src/components/common/Dataline/components/LogicalOperator';
import Shaping from 'src/components/common/Dataline/components/Shaping';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

export const DEFINITIONLINE_TEST_ID = 'definitionline';

interface DefinitionlineProps {
  comparisons: ComparisonFrontend[];
  definitionlineRenderId: string;
  isReadOnly?: boolean;
}
const Definitionline: FC<DefinitionlineProps> = ({
  comparisons,
  definitionlineRenderId,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('workspace');
  const { updateDataline } = useWorkspaceContext();

  const [{ isOver, canDrop }, definitionlineElement] = useDrop({
    accept: [DRAG_ITEM_TYPES.ORG_UNIT, DRAG_ITEM_TYPES.SHAPING],

    drop: (dragItem: DragItem, monitor) => {
      // Do nothing if the item was dropped on a child element of the current definitionline.
      if (monitor.didDrop() || !monitor.isOver({ shallow: true }) || isReadOnly)
        return;

      updateDataline(dragItem, definitionlineRenderId);
    },

    canDrop: () => !isReadOnly,

    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <Stack
      direction="row"
      sx={styles.definitionline(canDrop, isOver)}
      ref={definitionlineElement}
      data-test-id={DEFINITIONLINE_TEST_ID}
    >
      <DatalineFragment
        comparisons={comparisons}
        definitionlineRenderId={definitionlineRenderId}
        isReadOnly={isReadOnly}
      />
      {comparisons.map((comparison, index) => {
        const { op, renderId, valueNames, values, variable, variableName } =
          comparison;

        return (
          <Fragment key={renderId}>
            {index > 0 && (
              <LogicalOperatorWrapper
                comparisonRenderId={renderId}
                operator={op}
                isReadOnly={isReadOnly}
              />
            )}

            <Shaping
              isReadOnly={isReadOnly}
              shapingNames={valueNames}
              shapingValues={values}
              shapingRenderId={renderId}
              variable={variable}
              variableName={variableName}
            />
          </Fragment>
        );
      })}
      <Stack direction="column" sx={{ justifyContent: 'center', px: 2 }}>
        <Typography sx={styles.hint}>{t('dropHere').toUpperCase()}</Typography>
      </Stack>
    </Stack>
  );
};

export default Definitionline;
